// client/src/services/restoreCredential.ts
//
// GOOGLE PLAY QUALITY 2027 (scadenza Aprile 2027) — Zero-Tap Sign-In.
//
// Orchestrazione lato client delle "restore key" (Android Restore Credentials
// API via Credential Manager). Il plugin nativo è `RestoreCredential`
// (client/android/app/src/main/java/app/rideconnect/it/plugins/).
//
// REGOLA D'ORO: nessuna di queste funzioni deve mai bloccare o rallentare il
// flusso dell'utente. Ogni errore (plugin assente, device non compatibile,
// rete, server) degrada silenziosamente al login classico.
//
// Flussi:
//   registerRestoreKey()  → dopo il login: opzioni dal server → plugin.create
//                           → invio della chiave pubblica al server
//   tryRestoreSession()   → al primo avvio: opzioni → plugin.get → se trovata,
//                           il server verifica e risponde con la sessione
//   clearRestoreKey()     → al logout: revoca locale e server-side

import { Capacitor, registerPlugin } from "@capacitor/core";
import { apiFetch } from "@/api/core/apiFetch";
import useAuthStore from "@/store/useAuthStore";
import type { User } from "@/types";

// ─── Bridge verso il plugin nativo ──────────────────────────────────────────

interface CreateResult {
  supported: boolean;
  created?: boolean;
  cloudBackup?: boolean;
  registrationResponseJson?: string;
  reason?: string;
}

interface GetResult {
  supported: boolean;
  found?: boolean;
  authenticationResponseJson?: string;
  reason?: string;
}

interface ClearResult {
  supported: boolean;
  cleared?: boolean;
  reason?: string;
}

interface RestoreCredentialPlugin {
  create(options: { requestJson: string }): Promise<CreateResult>;
  get(options: { requestJson: string }): Promise<GetResult>;
  clear(): Promise<ClearResult>;
}

const NativeRestoreCredential = registerPlugin<RestoreCredentialPlugin>(
  "RestoreCredential",
);

/** La feature esiste solo su Android nativo (Credential Manager). */
export function isRestoreCredentialSupported(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

// ─── Registrazione (utente loggato) ─────────────────────────────────────────

/**
 * Registra la restore key dell'utente loggato.
 * Idempotente lato server (upsert): chiamarla più volte è sicuro.
 * @returns true se la chiave è stata registrata.
 */
export async function registerRestoreKey(): Promise<boolean> {
  if (!isRestoreCredentialSupported()) return false;

  try {
    const optionsRes = await apiFetch<{ options?: unknown }>(
      "/api/auth/restore-credential/register/options",
      { method: "POST" },
    );
    if (!optionsRes?.options) return false;

    const created = await NativeRestoreCredential.create({
      requestJson: JSON.stringify(optionsRes.options),
    });
    if (!created?.supported || !created.registrationResponseJson) return false;

    // Il server verifica l'attestazione e salva la chiave pubblica.
    await apiFetch("/api/auth/restore-credential/register", {
      method: "POST",
      body: JSON.stringify(JSON.parse(created.registrationResponseJson)),
    });
    return true;
  } catch {
    // Mai bloccare il login per una restore key
    return false;
  }
}

// ─── Ripristino (nuovo device, utente non loggato) ──────────────────────────

/**
 * Tenta il ripristino della sessione al primo avvio su un nuovo device.
 * @returns true se la sessione è stata ripristinata (utente loggato).
 */
export async function tryRestoreSession(): Promise<boolean> {
  if (!isRestoreCredentialSupported()) return false;

  try {
    const optionsRes = await apiFetch<{ options?: unknown }>(
      "/api/auth/restore-credential/options",
      { method: "POST" },
    );
    if (!optionsRes?.options) return false;

    const found = await NativeRestoreCredential.get({
      requestJson: JSON.stringify(optionsRes.options),
    });
    if (!found?.supported || !found.found || !found.authenticationResponseJson) {
      return false;
    }

    const session = await apiFetch<{ user: User; accessToken: string }>(
      "/api/auth/restore-credential/authenticate",
      {
        method: "POST",
        body: JSON.stringify(JSON.parse(found.authenticationResponseJson)),
      },
    );
    if (!session?.accessToken || !session.user) return false;

    // Stessa idratazione del login classico (token + user + storage nativo)
    useAuthStore.getState().setSession(session.user, session.accessToken);
    return true;
  } catch {
    return false;
  }
}

// ─── Revoca (logout) ────────────────────────────────────────────────────────

/**
 * Revoca la restore key. Da chiamare SEMPRE al logout: Credential Manager è
 * stateless e senza questa chiamata la chiave sopravviverebbe al sign-out.
 */
export async function clearRestoreKey(): Promise<void> {
  if (!isRestoreCredentialSupported()) return;

  try {
    await NativeRestoreCredential.clear();
  } catch {
    /* best-effort */
  }

  // Revoca anche lato server: una chiave rimossa dal device non deve poter
  // rigenerare una sessione se la chiave privata fosse compromessa.
  try {
    await apiFetch("/api/auth/restore-credential", { method: "DELETE" });
  } catch {
    /* best-effort */
  }
}
